import {
  getParameterConfigsSpecFromReadShape,
  UpdateAnalyticsDashboardSpecRequest,
} from 'insights/analytics-dashboards/RESTAnalyticsDashboardTypes';
import {AnalyticsQuerySpecRequest} from 'insights/analytics-queries/RESTAnalyticsQueryTypes';
import assertWellFormedLayout from 'insights/layouts/assertWellFormedLayout';
import syncLayoutToAnalyticsQueries from 'insights/layouts/syncLayoutToAnalyticsQueries';
import {AnalyticsParameterValue} from 'insights/parameters/__generated/AnalyticsParameterValue';
import getParameterValueObject from 'insights/parameters/data/getParameterValueObject';
import {getRenamedSQLQuery, getRenameInfos} from 'insights/parameters/data/RenameParameterConfig';
import syncParametersToSQLQueries from 'insights/parameters/data/syncParametersToSQLQueries';
import {AnalyticsParameterConfigSpecRequest} from 'insights/parameters/RESTParameterTypes';
import {omit} from 'util/omitKey';

import {AnalyticsParameterConfigGraphQL} from '../../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';
import {GraphQLAnalyticsDashboardSpec} from './__generated/GraphQLAnalyticsDashboardSpec';
import {convertGQLToRESTAnalyticsDashboard} from './AnalyticsDashboardGQLRESTConverters';

type GraphQLAnalyticsQueries = GraphQLAnalyticsDashboardSpec['analyticsQueries'][number];
type GraphQLLayoutRowSpec = GraphQLAnalyticsDashboardSpec['layoutRows'][number];

export function onUpdateAnalyticsDashboard(
  analyticsDashboard: GraphQLAnalyticsDashboardSpec
): UpdateAnalyticsDashboardSpecRequest {
  const restAnalyticsDashboard = convertGQLToRESTAnalyticsDashboard(analyticsDashboard);

  return {
    ...omit(restAnalyticsDashboard, 'modifiedAt', 'parameterConfigsGQL'),
    parameterConfigs: getParameterConfigsSpecFromReadShape(restAnalyticsDashboard.parameterConfigsGQL),
    clientModifiedAt: restAnalyticsDashboard.modifiedAt,
  };
}

function onTransformAnalyticsQueries(
  analyticsDashboard: GraphQLAnalyticsDashboardSpec,
  transformAnalyticsQueries: (
    oldAnalyticsQueries: Array<AnalyticsQuerySpecRequest>
  ) => Array<AnalyticsQuerySpecRequest>
): UpdateAnalyticsDashboardSpecRequest {
  assertWellFormedLayout(analyticsDashboard, 'before making spec request');

  const oldSpecRequest = onUpdateAnalyticsDashboard(analyticsDashboard);
  const newAnalyticsQueries = transformAnalyticsQueries(oldSpecRequest.analyticsQueries);
  const newLayoutRows = syncLayoutToAnalyticsQueries(oldSpecRequest.layoutRows, newAnalyticsQueries);
  const newSQLQueries = newAnalyticsQueries.map((analyticsQuery) => analyticsQuery.sqlQuery);
  const updateParametersData = syncParametersToSQLQueries(
    newSQLQueries,
    analyticsDashboard.parameterConfigs,
    analyticsDashboard.parameterValues
  );

  return {
    ...oldSpecRequest,
    analyticsQueries: newAnalyticsQueries,
    layoutRows: newLayoutRows,
    ...updateParametersData,
  };
}

export function onAddAnalyticsQuery(
  analyticsDashboard: GraphQLAnalyticsDashboardSpec,
  newAnalyticsQuerySpecRequest: AnalyticsQuerySpecRequest
): UpdateAnalyticsDashboardSpecRequest {
  return onTransformAnalyticsQueries(analyticsDashboard, (oldAnalyticsQueries) => [
    ...oldAnalyticsQueries,
    newAnalyticsQuerySpecRequest,
  ]);
}

export function onChangeAnalyticsQuery(
  analyticsDashboard: GraphQLAnalyticsDashboardSpec,
  analyticsQueryId: string,
  analyticsQuerySpecRequest: AnalyticsQuerySpecRequest
): UpdateAnalyticsDashboardSpecRequest {
  return onTransformAnalyticsQueries(analyticsDashboard, (oldAnalyticsQueries) =>
    oldAnalyticsQueries.map((eachAnalyticsQuery) =>
      eachAnalyticsQuery.id === analyticsQueryId
        ? {...analyticsQuerySpecRequest, id: analyticsQueryId}
        : eachAnalyticsQuery
    )
  );
}

export function onDeleteAnalyticsQuery(
  analyticsDashboard: GraphQLAnalyticsDashboardSpec,
  analyticsQueryId: string
): UpdateAnalyticsDashboardSpecRequest {
  return onTransformAnalyticsQueries(analyticsDashboard, (oldAnalyticsQueries) =>
    oldAnalyticsQueries.filter((eachAnalyticsQuery) => eachAnalyticsQuery.id !== analyticsQueryId)
  );
}

export function onChangeAnalyticsQueryAndParams(
  analyticsDashboard: GraphQLAnalyticsDashboardSpec,
  analyticsQueryId: string,
  analyticsQuerySpecRequest: AnalyticsQuerySpecRequest,
  newParameterConfigs: Array<AnalyticsParameterConfigSpecRequest>
): UpdateAnalyticsDashboardSpecRequest {
  const oldSpecRequest = onUpdateAnalyticsDashboard(analyticsDashboard);
  const newAnalyticsQueries = oldSpecRequest.analyticsQueries.map((analyticsQuery) => {
    if (analyticsQuery.id === analyticsQueryId) {
      return {
        ...analyticsQuerySpecRequest,
        id: analyticsQueryId,
      };
    }
    return analyticsQuery;
  });
  const newLayoutRows = syncLayoutToAnalyticsQueries(oldSpecRequest.layoutRows, newAnalyticsQueries);
  const newSQLQueries = newAnalyticsQueries.map((analyticsQuery) => analyticsQuery.sqlQuery);
  const updateParametersData = syncParametersToSQLQueries(
    newSQLQueries,
    analyticsDashboard.parameterConfigs,
    analyticsDashboard.parameterValues,
    newParameterConfigs
  );

  return {
    ...oldSpecRequest,
    analyticsQueries: newAnalyticsQueries,
    layoutRows: newLayoutRows,
    ...updateParametersData,
  };
}

export function onChangeLayoutRows(
  analyticsDashboard: GraphQLAnalyticsDashboardSpec,
  newLayoutRows: Array<GraphQLLayoutRowSpec>
): UpdateAnalyticsDashboardSpecRequest {
  const newDashboard = {
    ...analyticsDashboard,
    layoutRows: newLayoutRows,
  };
  assertWellFormedLayout(newDashboard, 'before making spec request');

  return onUpdateAnalyticsDashboard(newDashboard);
}

function getRenamedSqlQueries(
  analyticsQueries: Array<GraphQLAnalyticsQueries>,
  oldConfigs: Array<AnalyticsParameterConfigGraphQL>,
  newConfigs: Array<AnalyticsParameterConfigGraphQL>
): Array<GraphQLAnalyticsQueries> {
  const renameInfos = getRenameInfos(oldConfigs, newConfigs);

  return analyticsQueries.map((analyticsQuery) => ({
    ...analyticsQuery,
    sqlQuery: getRenamedSQLQuery(analyticsQuery.sqlQuery, renameInfos),
  }));
}

function updateParameterValuesForMultipleValueSetting(
  parameterConfigs: Array<AnalyticsParameterConfigGraphQL>,
  parameterValues: Array<AnalyticsParameterValue>
): Array<AnalyticsParameterValue> {
  const multiValueParametersConfigIds = new Set(
    parameterConfigs.filter((config) => config.isMulti).map((config) => config.id)
  );

  const updatedParameterValues = parameterValues.map((value) => {
    if (multiValueParametersConfigIds.has(value.config.id) && !Array.isArray(value.valueJson)) {
      return {
        ...value,
        valueJson: value.valueJson == null ? [] : [value.valueJson],
      };
    } else if (!multiValueParametersConfigIds.has(value.config.id) && Array.isArray(value.valueJson)) {
      return {
        ...value,
        valueJson: value.valueJson.length > 0 ? value.valueJson[0] : null,
      };
    }
    return value;
  });

  const newParameterValues = parameterConfigs
    .filter((config) => multiValueParametersConfigIds.has(config.id))
    .filter((config) => parameterValues.find((value) => value.config.id === config.id) == null)
    .map((config) => getParameterValueObject(config, []));

  return [...updatedParameterValues, ...newParameterValues];
}

export function onChangeParameterConfigsAndValues(
  analyticsDashboard: GraphQLAnalyticsDashboardSpec,
  newParameters: {
    configs?: Array<AnalyticsParameterConfigGraphQL>;
    values?: Array<AnalyticsParameterValue>;
  }
): UpdateAnalyticsDashboardSpecRequest {
  // If parameters were renamed, then rename them across the SQL queries
  const analyticsQueries =
    newParameters.configs != null
      ? getRenamedSqlQueries(
          analyticsDashboard.analyticsQueries,
          analyticsDashboard.parameterConfigs,
          newParameters.configs
        )
      : analyticsDashboard.analyticsQueries;

  const parameterConfigs: Array<AnalyticsParameterConfigGraphQL> =
    newParameters.configs ?? analyticsDashboard.parameterConfigs;
  const parameterValues: Array<AnalyticsParameterValue> = updateParameterValuesForMultipleValueSetting(
    parameterConfigs,
    newParameters.values ?? analyticsDashboard.parameterValues
  );

  return onUpdateAnalyticsDashboard({
    ...analyticsDashboard,
    analyticsQueries,
    parameterConfigs,
    parameterValues,
  });
}

export function onChangeParameterConfigs(
  analyticsDashboard: GraphQLAnalyticsDashboardSpec,
  newParameterConfigs: Array<AnalyticsParameterConfigGraphQL>
): UpdateAnalyticsDashboardSpecRequest {
  return onChangeParameterConfigsAndValues(analyticsDashboard, {configs: newParameterConfigs});
}

export function onChangeParameterValues(
  analyticsDashboard: GraphQLAnalyticsDashboardSpec,
  newParameterValues: Array<AnalyticsParameterValue>
): UpdateAnalyticsDashboardSpecRequest {
  return onChangeParameterConfigsAndValues(analyticsDashboard, {values: newParameterValues});
}
